<template>
  <div class="mx-2 screen-cover pt-8">
    <div v-if="policyDetail" class="PolicyDetail mx-6">
      <div class="mb-6">
        <router-link class="breadcrumb-link" to="/publisher/policies">
          Policies
        </router-link>
        <span class="breadcrumb-separator">/</span>
        <span class="breadcrumb-link" v-if="policyDetail">{{
          policyDetail.name
        }}</span>
      </div>
      <div
        class="d-flex flex-column flex-sm-row gap-3 align-sm-center w-full section-block mb-6 justify-space-between"
      >
        <div class="d-flex align-center">
          <div class="PolicyDetail__icon-button pa-2 mr-4">
            <the-icon icon="document" class="PolicyDetail__icon" />
          </div>
          <div>
            <h3>{{ policyDetail.name }}</h3>
            <p class="mt-2 mb-0">{{ formatDate(policyDetail.updatedAt) }}</p>
            <p class="mt-1 mb-0 text-sm" v-if="policyDetail.scope">
              Scope: {{ policyDetail.scope }}
            </p>
            <p class="mt-1 mb-0 text-sm" v-if="policyDetail.active !== undefined">
              Status: {{ policyDetail.active ? 'Active' : 'Inactive' }}
            </p>
          </div>
        </div>

        <v-btn class="v-btn exportBtn" @click="onExport" elevation="2" large>
          <v-icon dark left> mdi-export-variant </v-icon>
          Export
        </v-btn>
      </div>
      <!-- End of policy Head section -->

      <v-row>
        <v-col cols="12" sm="6">
          <div class="section-block">
            <h6 class="PolicyDetail__section-heading">Policy Set Information</h6>
            <div class="PolicyDetail__info-row">
              <span class="PolicyDetail__label">Name</span>
              <div class="PolicyDetail__info-value">
                {{ policyDetail.name }}
              </div>
            </div>
            <div class="PolicyDetail__info-row">
              <span class="PolicyDetail__label">Scope</span>
              <div class="PolicyDetail__info-value">
                {{ policyDetail.scope || 'N/A' }}
              </div>
            </div>
            <div class="PolicyDetail__info-row">
              <span class="PolicyDetail__label">Status</span>
              <div class="PolicyDetail__info-value">
                {{ policyDetail.active ? 'Active' : 'Inactive' }}
              </div>
            </div>
            <div class="PolicyDetail__info-row">
              <span class="PolicyDetail__label">Updated</span>
              <div class="PolicyDetail__info-value">
                {{ formatDate(policyDetail.updatedAt) }}
              </div>
            </div>
          </div>
          
          <div class="section-block mt-6">
            <div class="d-flex justify-space-between align-center mb-4">
              <h6 class="PolicyDetail__section-heading mb-0">Policy Rules</h6>
              <v-btn
                v-if="isPublisherAdmin"
                small
                color="primary"
                @click="openRuleDialog()"
                elevation="2"
              >
                <v-icon small left>mdi-plus</v-icon>
                Add Rule
              </v-btn>
            </div>
            <div v-if="policyRules && policyRules.length > 0">
              <div v-for="(rule, index) in policyRules" :key="rule.id || index" class="mb-4 PolicyDetail__rule-card">
                <div class="PolicyDetail__rule-card-content">
                  <div class="PolicyDetail__info-row">
                    <span class="PolicyDetail__label">Rule Type</span>
                    <div class="PolicyDetail__info-value">
                      <v-chip x-small color="primary" outlined>{{ rule.rule_type || 'N/A' }}</v-chip>
                    </div>
                  </div>
                  <div class="PolicyDetail__info-row">
                    <span class="PolicyDetail__label">Severity</span>
                    <div class="PolicyDetail__info-value">
                      <v-chip x-small :color="getSeverityColor(rule.severity)" dark>{{ rule.severity || 'N/A' }}</v-chip>
                    </div>
                  </div>
                  <div v-if="rule.config" class="PolicyDetail__info-row">
                    <span class="PolicyDetail__label">Configuration</span>
                    <div class="PolicyDetail__info-value">
                      <pre class="text-xs PolicyDetail__config-preview">{{ JSON.stringify(rule.config, null, 2) }}</pre>
                    </div>
                  </div>
                </div>
                <div v-if="isPublisherAdmin" class="PolicyDetail__rule-card-footer">
                  <v-btn
                    icon
                    x-small
                    @click="openRuleDialog(rule)"
                    color="primary"
                    class="PolicyDetail__rule-action-btn"
                  >
                    <v-icon x-small>mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    x-small
                    @click="confirmDeleteRule(rule)"
                    color="error"
                    class="PolicyDetail__rule-action-btn"
                  >
                    <v-icon x-small>mdi-delete</v-icon>
                  </v-btn>
                </div>
                <v-divider v-if="index < policyRules.length - 1" class="mt-4"></v-divider>
              </div>
            </div>
            <div v-else>
              <p class="text-sm text-gray-500">No rules configured for this policy set.</p>
            </div>
          </div>
        </v-col>
        <v-col cols="12" sm="6">
          <div class="section-block">
            <h6 class="PolicyDetail__section-heading">Plugins Using Policy</h6>
            <div>
              <v-data-table
                :headers="headers"
                :items="pluginsList"
                class="plugins-list-table"
                hide-default-footer
                :sort_by="['name']"
              >
                <template v-slot:item.name="{ item }">
                  <div class="plugin-link" @click="onNavigateToPlugin(item)">
                    {{ item.name }}
                  </div>
                </template>
                <template v-slot:item.audit="{ item }">
                  <div
                    v-for="index in 4"
                    :key="index"
                    class="audit-chip"
                    :class="getAuditClass()"
                  />
                </template>
              </v-data-table>
            </div>
          </div>
        </v-col>
      </v-row>
    </div>
    <div v-if="loading" class="spinner">
      <FlamePreloader :compact="true" :size="100" />
    </div>

    <!-- Policy Rule Dialog -->
    <v-dialog v-model="ruleDialogOpen" max-width="800px" persistent>
      <v-card dark class="PolicyDetail__rule-dialog">
        <v-card-title class="PolicyDetail__rule-dialog-title">
          <span class="headline">{{ editingRule ? 'Edit Policy Rule' : 'Create Policy Rule' }}</span>
        </v-card-title>
        <v-card-text class="PolicyDetail__rule-dialog-content">
          <v-form ref="ruleForm" v-model="ruleFormValid">
            <v-select
              v-model="ruleForm.rule_type"
              :items="ruleTypes"
              label="Rule Type"
              required
              :rules="[v => !!v || 'Rule type is required']"
              outlined
              dense
              dark
              class="mb-4"
            ></v-select>

            <v-select
              v-model="ruleForm.severity"
              :items="severityOptions"
              label="Severity"
              required
              :rules="[v => !!v || 'Severity is required']"
              outlined
              dense
              dark
              class="mb-4"
            ></v-select>

            <div class="mb-4">
              <label class="PolicyDetail__rule-dialog-label mb-2 d-block">Configuration (JSON)</label>
              <div class="PolicyDetail__codemirror-wrapper">
                <codemirror
                  v-model="ruleForm.configJson"
                  :options="codeMirrorOptions"
                  @input="onConfigJsonChange"
                />
              </div>
              <div class="PolicyDetail__rule-dialog-help mt-2">
                <div class="text-xs text-gray-400 mb-1">Examples:</div>
                <ul class="text-xs text-gray-500">
                  <li><strong>Version Drift:</strong> <code>{"risk_level_actions": {"critical": "quarantine", "high": "quarantine", "medium": "require_review", "low": "flag_for_review"}}</code></li>
                  <li><strong>Permissions:</strong> <code>{"allowed_permissions": ["read:site"], "restricted_permissions": ["write:all"]}</code></li>
                  <li><strong>Outbound Domains:</strong> <code>{"allowed_domains": ["https://api.getforge.com"], "blocked_domains": ["http://*"]}</code></li>
                </ul>
              </div>
            </div>
          </v-form>
        </v-card-text>
        <v-card-actions class="PolicyDetail__rule-dialog-actions">
          <v-spacer></v-spacer>
          <v-btn text dark @click="closeRuleDialog">Cancel</v-btn>
          <v-btn
            color="primary"
            dark
            @click="saveRule"
            :disabled="!ruleFormValid || savingRule || !isConfigJsonValid"
            :loading="savingRule"
          >
            {{ editingRule ? 'Update' : 'Create' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import dayjs from 'dayjs'
import FlamePreloader from '@/components/_Common/FlamePreloader'
import { MODAL_ALERT } from '@/components/Modals'
import marketplaceAPI from '@/services/marketplace'
import { codemirror } from 'vue-codemirror'
import 'codemirror/lib/codemirror.css'
import 'codemirror/mode/javascript/javascript.js'
import 'codemirror/theme/base16-dark.css'

export default {
  name: 'PolicyDetail',
  components: {
    FlamePreloader,
    codemirror,
  },
  data() {
    return {
      policyDetail: null,
      policyRules: [],
      loading: false,
      pluginsList: [],
      columns: ['name', 'url', 'audit'],
      columnOptions: [
        { text: 'NAME', value: 'name' },
        { text: 'URL', value: 'url' },
        { text: 'Audit', value: 'audit' },
      ],
      // Rule management
      ruleDialogOpen: false,
      editingRule: null,
      ruleFormValid: false,
      savingRule: false,
      ruleForm: {
        rule_type: null,
        severity: null,
        configJson: '',
      },
      isConfigJsonValid: true,
      ruleTypes: [
        { text: 'Version Drift', value: 'version_drift' },
        { text: 'Permissions', value: 'permissions' },
        { text: 'Outbound Domains', value: 'outbound_domains' },
        { text: 'Obfuscation', value: 'obfuscation' },
        { text: 'Attestation', value: 'attestation' },
        { text: 'Resource Limits', value: 'resource_limits' },
      ],
      severityOptions: [
        { text: 'Pass', value: 'pass' },
        { text: 'Warn', value: 'warn' },
        { text: 'Fail', value: 'fail' },
      ],
      codeMirrorOptions: {
        tabSize: 2,
        mode: {
          name: 'javascript',
          json: true,
        },
        theme: 'base16-dark',
        lineNumbers: true,
        lineWrapping: true,
        indentUnit: 2,
        autoCloseBrackets: true,
        matchBrackets: true,
      },
    }
  },
  computed: {
    ...mapState({
      currentPublisherInfo: ({ publisher_infos }) => publisher_infos.current,
    }),
    isPublisherAdmin() {
      return this.currentPublisherInfo.role === 'admin'
    },
    headers() {
      // Plugin list table header to be calculated selected columns filter
      const { columns, columnOptions } = this
      const options = columnOptions.reduce(
        (acc, cur) =>
          cur.children ? [...acc, ...cur.children] : [...acc, cur],
        []
      )
      return columns.map((column) =>
        options.find((option) => option.value === column)
      )
    },
  },
  async mounted() {
    this.loading = true
    try {
      await this.getPolicyDetail()
      await this.getPluginsListByPolicy()
    } catch (error) {
      console.error('Error loading policy detail:', error)
      this.$store.commit('application/openModal', {
        component: MODAL_ALERT,
        props: {
          title: 'Error',
          text: error.message || 'Failed to load policy details',
          actionBtnLabel: 'OK',
          showCancelBtn: false,
        },
      })
    } finally {
      this.loading = false
    }
  },
  methods: {
    async getPolicyDetail() {
      try {
        const policySetId = this.$route.params.policyId
        const policySet = await marketplaceAPI.getPolicySet(policySetId)
        
        // Map policy set to the format expected by the component
        this.policyDetail = {
          id: policySet.id,
          name: policySet.name || 'Unnamed Policy Set',
          updatedAt: policySet.updated_at || policySet.created_at,
          active: policySet.active,
          scope: policySet.scope,
          // Store the full policy set for reference
          _policySet: policySet,
        }
        
        // Get policy rules for this policy set
        // The API might return rules nested in the policy set response
        // or we might need to fetch them separately
        if (policySet.policy_rules && policySet.policy_rules.length > 0) {
          this.policyRules = policySet.policy_rules
        } else {
          // If rules aren't included, fetch them separately
          try {
            this.policyRules = await marketplaceAPI.listPolicyRules(policySetId)
          } catch (error) {
            console.warn('Could not fetch policy rules:', error)
            this.policyRules = []
          }
        }
      } catch (error) {
        console.error('Error fetching policy set:', error)
        throw error
      }
    },

    getBooleanPropertyLabel(value) {
      if (value === null || value === undefined || value === '') return ''
      return value ? 'Yes' : 'No'
    },

    onExport() {},
    formatDate(date) {
      if (!date) return ''
      return dayjs(date).format('D MMM YYYY hh:mm')
    },
    
    async getPluginsListByPolicy() {
      try {
        const policySetId = this.$route.params.policyId
        
        // Find plugins that have been evaluated against this policy set
        // by searching policy evaluations for plugin versions
        const evaluationsResponse = await marketplaceAPI.listPolicyEvaluations({
          policy_set_id: policySetId,
          target_type: 'plugin_version',
        })
        
        if (!evaluationsResponse.evaluations || evaluationsResponse.evaluations.length === 0) {
          this.pluginsList = []
          return
        }
        
        // Extract unique plugin version IDs from evaluations
        const versionIds = new Set()
        evaluationsResponse.evaluations.forEach((evaluation) => {
          if (evaluation.target_id) {
            versionIds.add(evaluation.target_id)
          }
        })
        
        // Get all plugins and check their versions
        const allPlugins = await marketplaceAPI.listPlugins({})
        const pluginMap = new Map()
        
        // For each plugin, check if any of its versions have been evaluated
        for (const plugin of allPlugins) {
          try {
            const versions = await marketplaceAPI.getPluginVersions(plugin.slug)
            const hasEvaluation = versions.some((version) => versionIds.has(version.id))
            
            if (hasEvaluation && !pluginMap.has(plugin.slug)) {
              pluginMap.set(plugin.slug, {
                name: plugin.name,
                slug: plugin.slug,
                url: plugin.entrypoint_url || plugin.iframe_src || '',
              })
            }
          } catch (err) {
            // Skip if we can't get versions for this plugin
            console.warn(`Could not get versions for plugin ${plugin.slug}:`, err)
          }
        }
        
        // Convert to array format expected by the component
        this.pluginsList = Array.from(pluginMap.values())
      } catch (error) {
        console.error('Error fetching plugins by policy:', error)
        // Don't throw - just show empty list
        this.pluginsList = []
      }
    },

    getAuditClass() {
      const audit = Math.floor(Math.random() * 4)
      switch (audit) {
        case 0:
          return 'PolicyDetail__audit-green'
        case 1:
          return 'PolicyDetail__audit-red'
        case 2:
          return 'PolicyDetail__audit-blue'
        case 3:
          return 'PolicyDetail__audit-orange'
      }
    },

    onNavigateToPlugin(value) {
      this.$router.push({
        name: 'plugin_detail',
        params: { pluginId: value.slug },
      })
    },

    // ============================================
    // Policy Rule Management
    // ============================================

    getSeverityColor(severity) {
      const colors = {
        pass: 'success',
        warn: 'warning',
        fail: 'error',
      }
      return colors[severity] || 'grey'
    },

    openRuleDialog(rule = null) {
      this.editingRule = rule
      if (rule) {
        // Editing existing rule
        this.ruleForm = {
          rule_type: rule.rule_type,
          severity: rule.severity,
          configJson: JSON.stringify(rule.config || {}, null, 2),
        }
        this.isConfigJsonValid = true
      } else {
        // Creating new rule - set default JSON structure based on rule type
        this.ruleForm = {
          rule_type: null,
          severity: null,
          configJson: '{}',
        }
        this.isConfigJsonValid = true
      }
      this.ruleDialogOpen = true
    },

    closeRuleDialog() {
      this.ruleDialogOpen = false
      this.editingRule = null
      this.ruleForm = {
        rule_type: null,
        severity: null,
        configJson: '',
      }
      this.isConfigJsonValid = true
      if (this.$refs.ruleForm) {
        this.$refs.ruleForm.resetValidation()
      }
    },

    onConfigJsonChange(value) {
      // Validate JSON on change
      if (!value || value.trim() === '') {
        // Allow empty JSON - will be saved as empty object
        this.isConfigJsonValid = true
        return
      }
      try {
        JSON.parse(value)
        this.isConfigJsonValid = true
      } catch (e) {
        this.isConfigJsonValid = false
      }
    },

    validateJson(value) {
      if (!value || value.trim() === '') {
        return 'Configuration is required'
      }
      try {
        JSON.parse(value)
        return true
      } catch (e) {
        return 'Invalid JSON format'
      }
    },

    async saveRule() {
      if (!this.$refs.ruleForm.validate()) {
        return
      }

      // Validate JSON before saving
      if (!this.isConfigJsonValid) {
        this.$store.commit('application/openModal', {
          component: MODAL_ALERT,
          props: {
            title: 'Invalid JSON',
            text: 'Please fix the JSON configuration errors before saving.',
            actionBtnLabel: 'OK',
            showCancelBtn: false,
          },
        })
        return
      }

      this.savingRule = true
      try {
        const policySetId = this.$route.params.policyId
        let config = {}
        
        if (this.ruleForm.configJson && this.ruleForm.configJson.trim()) {
          try {
            config = JSON.parse(this.ruleForm.configJson)
          } catch (e) {
            throw new Error('Invalid JSON configuration: ' + e.message)
          }
        }

        const ruleData = {
          rule_type: this.ruleForm.rule_type,
          severity: this.ruleForm.severity,
          config: config,
        }

        if (this.editingRule) {
          // Update existing rule
          await marketplaceAPI.updatePolicyRule(policySetId, this.editingRule.id, ruleData)
        } else {
          // Create new rule
          await marketplaceAPI.createPolicyRule(policySetId, ruleData)
        }

        // Refresh rules list
        await this.getPolicyDetail()
        
        this.closeRuleDialog()
        
        this.$store.commit('application/openModal', {
          component: MODAL_ALERT,
          props: {
            title: 'Success',
            text: `Policy rule ${this.editingRule ? 'updated' : 'created'} successfully`,
            actionBtnLabel: 'OK',
            showCancelBtn: false,
          },
        })
      } catch (error) {
        console.error('Error saving policy rule:', error)
        this.$store.commit('application/openModal', {
          component: MODAL_ALERT,
          props: {
            title: 'Error',
            text: error.message || `Failed to ${this.editingRule ? 'update' : 'create'} policy rule`,
            actionBtnLabel: 'OK',
            showCancelBtn: false,
          },
        })
      } finally {
        this.savingRule = false
      }
    },

    confirmDeleteRule(rule) {
      this.$store.commit('application/openModal', {
        component: MODAL_ALERT,
        props: {
          title: 'Delete Policy Rule',
          text: `Are you sure you want to delete this ${rule.rule_type} rule? This action cannot be undone.`,
          actionBtnLabel: 'Delete',
          showCancelBtn: true,
          action: () => this.deleteRule(rule),
        },
      })
    },

    async deleteRule(rule) {
      try {
        const policySetId = this.$route.params.policyId
        await marketplaceAPI.deletePolicyRule(policySetId, rule.id)
        
        // Refresh rules list
        await this.getPolicyDetail()
        
        this.$store.commit('application/openModal', {
          component: MODAL_ALERT,
          props: {
            title: 'Success',
            text: 'Policy rule deleted successfully',
            actionBtnLabel: 'OK',
            showCancelBtn: false,
          },
        })
      } catch (error) {
        console.error('Error deleting policy rule:', error)
        this.$store.commit('application/openModal', {
          component: MODAL_ALERT,
          props: {
            title: 'Error',
            text: error.message || 'Failed to delete policy rule',
            actionBtnLabel: 'OK',
            showCancelBtn: false,
          },
        })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.screen-cover {
  position: relative;
  min-height: 100vh;
}
.section-block {
  border: 1px solid $N5;
  background: $N4;
  border-radius: 4px;
  padding: 24px;
}
$style: PolicyDetail;
.#{$style} {
  &__icon-button {
    background: $N5;
    width: 64px;
    height: 64px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
  }
  &__icon {
    color: $B1;
    fill: $B1;
  }
  h3 {
    font-size: 20px;
    line-height: 24px;
  }
  p {
    font-size: 14px;
    line-height: 22px;
  }
  .v-btn {
    display: block;
    margin-left: 10px;
  }
  .exportBtn {
    color: $B1;
    background: $N5;
  }
  &__label {
    width: 200px;
    flex-shrink: 0;
    color: $N28;
    font-size: 14px;
  }
  &__section-heading {
    color: white;
    font-size: 16px;
    line-height: 20px;
    margin-bottom: 24px;
    font-weight: normal;
  }
  &__info-row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 16px;
  }

  &__rule-card {
    position: relative;
    padding: 16px;
    background: rgba(255, 255, 255, 0.02);
    border-radius: 4px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    transition: border-color 0.2s ease;
    
    &:hover {
      border-color: rgba(255, 255, 255, 0.2);
      
      .PolicyDetail__rule-card-footer {
        opacity: 1;
        visibility: visible;
      }
    }
  }

  &__rule-card-content {
    width: 100%;
  }

  &__rule-card-footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.2s ease, visibility 0.2s ease;
  }

  &__rule-action-btn {
    opacity: 0.8;
    transition: opacity 0.2s ease;
    
    &:hover {
      opacity: 1;
    }
  }

  &__config-preview {
    max-width: 400px;
    overflow-x: auto;
    background: rgba(0, 0, 0, 0.3);
    padding: 8px;
    border-radius: 4px;
    font-size: 11px;
    line-height: 1.4;
  }

  &__audit-green {
    background: $G1;
  }
  &__audit-red {
    background: $R1;
  }
  &__audit-blue {
    background: $B1;
  }
  &__audit-orange {
    background: $Y1;
  }

  &__rule-dialog {
    background: $N4 !important;
    
    .v-card__title {
      background: $N4;
      border-bottom: 1px solid $N5;
      padding: 24px;
    }

    .v-card__text {
      background: $N4;
      padding: 24px;
    }

    .v-card__actions {
      background: $N4;
      border-top: 1px solid $N5;
      padding: 16px 24px;
    }
  }

  &__rule-dialog-title {
    color: white;
  }

  &__rule-dialog-content {
    color: white;
  }

  &__rule-dialog-label {
    color: $N28;
    font-size: 14px;
    font-weight: 500;
  }

  &__rule-dialog-help {
    code {
      background: rgba(0, 0, 0, 0.3);
      padding: 2px 6px;
      border-radius: 3px;
      font-size: 11px;
      color: $B1;
    }
  }

  &__codemirror-wrapper {
    border: 1px solid $N5;
    border-radius: 4px;
    overflow: hidden;
    
    // Apply default CodeMirror styles - ensure proper gutter spacing
    :deep(.CodeMirror) {
      height: auto;
      min-height: 300px;
      
      .CodeMirror-scroll {
        min-height: 300px;
      }
      
      // Move gutter wrapper to the left to create more space
      .CodeMirror-gutter-wrapper {
        left: -30px !important;
      }
      
      // Gutter: ensure proper width and positioning
      .CodeMirror-gutters {
        width: 3em;
        left: 0;
      }
      
      // Line numbers: right-align within gutter with proper padding
      .CodeMirror-linenumber {
        text-align: right;
        padding-right: 8px;
        min-width: 2em;
        left: -15px !important;
      }
      
      // Gutter element positioning
      .CodeMirror-gutter-elt {
        left: -15px !important;
      }
      
      // Ensure sizer has proper left margin to account for gutter
      // CodeMirror should do this automatically, but ensure it's set
      .CodeMirror-sizer {
        margin-left: 3em !important; // Match gutter width exactly
      }
    }
  }

  &__rule-dialog-actions {
    .v-btn {
      color: white;
    }
  }
}

.plugins-list-table {
  background: #1f1e31;
  border: 1px solid #2e2c42;
  header {
    background-color: #1f1e31 !important;
  }
}

.spinner {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  background: rgba(200, 200, 200, 0.3);
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  z-index: 80;
}

.plugin-link {
  cursor: pointer;
  color: $B1;
}
</style>
